import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useEditorAuth } from "@/context/EditorAuthContext";
import { ChevronLeft, ChevronRight } from "lucide-react";

function startOfMonth(y, m) { return new Date(y, m, 1); }
function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }

export default function CalendarPanel() {
  const { authAxios } = useEditorAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-11

  useEffect(() => {
    setLoading(true);
    authAxios().get("/editor/calendar").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const firstDay = startOfMonth(year, month);
  const startWeekday = firstDay.getDay(); // 0 Sun
  const dim = daysInMonth(year, month);
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const byDay = {};
  items.forEach((it) => {
    const dstr = it.publish_at || it.published_at;
    if (!dstr) return;
    const d = new Date(dstr);
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate();
      byDay[day] = byDay[day] || [];
      byDay[day].push(it);
    }
  });

  const prev = () => {
    if (month === 0) { setYear(year - 1); setMonth(11); } else setMonth(month - 1);
  };
  const next = () => {
    if (month === 11) { setYear(year + 1); setMonth(0); } else setMonth(month + 1);
  };

  const monthName = firstDay.toLocaleString("en-SG", { month: "long", year: "numeric" });
  const scheduledCount = items.filter(i => i.status === "scheduled").length;
  const publishedCount = items.filter(i => i.status === "published").length;

  return (
    <div data-testid="calendar-panel">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div className="text-sm text-gray-500">{items.length} on calendar · {scheduledCount} scheduled · {publishedCount} published</div>
        <div className="flex items-center gap-4">
          <button onClick={prev} data-testid="cal-prev" className="w-9 h-9 border border-[#0A1128] flex items-center justify-center hover:bg-[#0A1128] hover:text-white transition"><ChevronLeft className="w-4 h-4"/></button>
          <div className="font-serif text-xl text-[#0A1128] w-48 text-center" data-testid="cal-month">{monthName}</div>
          <button onClick={next} data-testid="cal-next" className="w-9 h-9 border border-[#0A1128] flex items-center justify-center hover:bg-[#0A1128] hover:text-white transition"><ChevronRight className="w-4 h-4"/></button>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : (
        <div className="bg-white border border-gray-200">
          <div className="grid grid-cols-7 border-b border-gray-200 text-xs tracking-widest uppercase text-gray-500">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
              <div key={d} className="p-3 text-center border-r border-gray-200 last:border-0">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              const isToday = d && d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
              return (
                <div key={i} className={`min-h-[110px] border-r border-b border-gray-100 p-2 last:border-r-0 ${!d ? "bg-[#F9F9F7]" : ""}`}>
                  {d && (
                    <>
                      <div className={`text-xs mb-1 ${isToday ? "font-bold text-[#688A4E]" : "text-gray-400"}`}>{d}</div>
                      <div className="space-y-1">
                        {(byDay[d] || []).map((a) => (
                          <Link
                            key={a.id}
                            to={`/editor/articles/${a.id}`}
                            data-testid={`cal-article-${a.slug}`}
                            className={`block text-[10px] px-1.5 py-1 truncate ${
                              a.status === "scheduled" ? "bg-amber-50 border-l-2 border-amber-500 text-amber-800 hover:bg-amber-100" :
                              "bg-[#E8ECE1] border-l-2 border-[#688A4E] text-[#3d5228] hover:bg-[#d8dfc8]"
                            }`}
                            title={a.title}
                          >{a.title}</Link>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-6 text-xs text-gray-500">
        <div className="flex items-center gap-2"><span className="w-3 h-3 bg-[#E8ECE1] border-l-2 border-[#688A4E]"></span> Published</div>
        <div className="flex items-center gap-2"><span className="w-3 h-3 bg-amber-50 border-l-2 border-amber-500"></span> Scheduled</div>
      </div>
    </div>
  );
}
